import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import { priceHistoryAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const sourceLabels = {
    reception: 'Réception commande',
    manual_reception: 'Réception manuelle',
    manual_edit: 'Édition manuelle',
};

export default function PriceHistoryPanel({ supplierProductId }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!supplierProductId) return;
        setLoading(true);
        priceHistoryAPI.getByProduct(supplierProductId)
            .then(res => setEntries(res.data || []))
            .catch(() => setEntries([]))
            .finally(() => setLoading(false));
    }, [supplierProductId]);

    const stats = useMemo(() => {
        if (entries.length === 0) return null;
        const prices = entries.map(e => e.unit_price);
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const current = prices[prices.length - 1];
        const first = prices[0];
        const totalVariation = first > 0 ? ((current - first) / first) * 100 : 0;
        return { min, max, current, first, totalVariation };
    }, [entries]);

    const chartPath = useMemo(() => {
        if (entries.length < 2) return null;
        const prices = entries.map(e => e.unit_price);
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const range = max - min || 1;
        const w = 300;
        const h = 60;
        const points = prices.map((p, i) => {
            const x = (i / (prices.length - 1)) * w;
            const y = h - ((p - min) / range) * h;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        });
        return { d: `M${points.join(' L')}`, w, h, points: prices.map((p, i) => {
            const x = (i / (prices.length - 1)) * w;
            const y = h - ((p - min) / range) * h;
            return { x, y, price: p, date: entries[i].date };
        }) };
    }, [entries]);

    if (loading) {
        return <div className="py-4 text-center text-slate-400 text-xs"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>;
    }

    if (entries.length === 0) {
        return (
            <div className="mt-3 p-3 bg-slate-50 rounded border border-slate-200 text-xs text-slate-500 text-center">
                Aucun historique de prix disponible pour ce produit.
            </div>
        );
    }

    return (
        <div className="mt-3 border rounded-lg overflow-hidden" data-testid="price-history-panel">
            <div className="px-3 py-2 bg-slate-100 border-b text-xs font-semibold text-slate-700 flex items-center justify-between">
                <span>Historique des tarifs ({entries.length} entrée{entries.length > 1 ? 's' : ''})</span>
                {stats && (
                    <span className={`flex items-center gap-1 text-xs font-bold ${stats.totalVariation > 0.1 ? 'text-red-600' : stats.totalVariation < -0.1 ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {stats.totalVariation > 0.1 ? <TrendingUp className="w-3.5 h-3.5" /> : stats.totalVariation < -0.1 ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                        {stats.totalVariation > 0 ? '+' : ''}{stats.totalVariation.toFixed(1)}% depuis la 1ère réception
                    </span>
                )}
            </div>

            {/* Mini-chart */}
            {chartPath && (
                <div className="p-3 bg-white border-b">
                    <svg viewBox={`0 0 ${chartPath.w} ${chartPath.h}`} className="w-full h-16" preserveAspectRatio="none">
                        <path d={chartPath.d} fill="none" stroke="#f97316" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                        {chartPath.points.map((pt, i) => (
                            <circle key={i} cx={pt.x} cy={pt.y} r="2.5" fill="#f97316">
                                <title>{pt.date} : {formatCurrency(pt.price)}</title>
                            </circle>
                        ))}
                    </svg>
                    {stats && (
                        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                            <span>Min : {formatCurrency(stats.min)}</span>
                            <span>Actuel : <strong className="text-slate-700">{formatCurrency(stats.current)}</strong></span>
                            <span>Max : {formatCurrency(stats.max)}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Table */}
            <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                        <tr>
                            <th className="text-left px-2 py-1">Date</th>
                            <th className="text-right px-2 py-1">Prix</th>
                            <th className="text-right px-2 py-1">Variation</th>
                            <th className="text-left px-2 py-1">Source</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...entries].reverse().map(e => (
                            <tr key={e.id} className="border-t hover:bg-slate-50">
                                <td className="px-2 py-1 text-slate-600">{e.date || '-'}</td>
                                <td className="px-2 py-1 text-right font-medium text-slate-800">{formatCurrency(e.unit_price)}</td>
                                <td className="px-2 py-1 text-right">
                                    {e.variation_pct == null ? (
                                        <span className="text-slate-300">—</span>
                                    ) : (
                                        <span className={`font-semibold ${e.variation_pct > 0.1 ? 'text-red-600' : e.variation_pct < -0.1 ? 'text-emerald-600' : 'text-slate-500'}`}>
                                            {e.variation_pct > 0 ? '+' : ''}{e.variation_pct}%
                                        </span>
                                    )}
                                </td>
                                <td className="px-2 py-1 text-slate-500">
                                    {sourceLabels[e.source] || e.source}
                                    {e.document_number && <span className="ml-1 text-slate-400">· {e.document_number}</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
